/**
 * Test mode service — checks if test mode is active and renders a warning banner.
 * Used by apps that have a test/sandbox mode (e.g. Compliance).
 */
import { t } from '../i18n/index.js';

let testModeActive = false;

/**
 * Check the server for test mode status.
 * @returns {Promise<boolean>}
 */
export async function checkTestMode() {
  try {
    // i18n-fetch-ok: config GET, no user-visible server text
    const res = await fetch('/api/test-mode');
    if (res.ok) {
      const data = await res.json();
      testModeActive = data.test_mode === true;
    }
  } catch {
    // ignore — test mode defaults to off
  }
  return testModeActive;
}

/**
 * Return current test mode state (cached from last check).
 */
export function isTestMode() {
  return testModeActive;
}

/**
 * Set test mode programmatically and update the banner.
 */
export function setTestMode(active) {
  testModeActive = active;
  renderTestBanner();
}

let bannerResize = null;

/**
 * Publish the bar's RENDERED height as `--env-banner-h` and flag the body,
 * which is what `layout.css` uses to push the page out from under it.
 *
 * ⚠️ Measured, never assumed. The bar wraps to two lines on a narrow screen
 * (and the beta bar carries an extra "Exit beta" link), so a hardcoded
 * height under-shoots exactly where the overlap is worst — a phone. A
 * ResizeObserver keeps it right through rotation, zoom and font scaling.
 */
function trackBannerHeight(bar) {
  const apply = () => {
    const h = Math.ceil(bar.getBoundingClientRect().height);
    document.documentElement.style.setProperty('--env-banner-h', `${h}px`);
  };
  apply();
  if (typeof ResizeObserver === 'function') {
    bannerResize = new ResizeObserver(apply);
    bannerResize.observe(bar);
  } else {
    bannerResize = { disconnect: () => window.removeEventListener('resize', apply) };
    window.addEventListener('resize', apply);
  }
}

/**
 * Remove the bar and undo its displacement. Both halves, always — leaving
 * `--env-banner-h` set with no bar on screen puts a strip of dead space at
 * the top of the app.
 */
function clearBanner() {
  if (bannerResize) { bannerResize.disconnect(); bannerResize = null; }
  const existing = document.getElementById('test-mode-banner');
  if (existing) existing.remove();
  document.body.classList.remove('has-env-banner');
  document.documentElement.style.removeProperty('--env-banner-h');
}

/**
 * Paint the one fixed-top env banner. There is a single `#test-mode-banner`
 * element by design — three stacked fixed bars at top:0 would sit on top of
 * each other, so `checkEnvBanner` picks ONE state to report.
 *
 * The bar is `position: fixed` so it survives scrolling; the page is pushed
 * down by `body.has-env-banner` in layout.css rather than being covered.
 *
 * @param {string} background  CSS colour for the bar
 * @param {string} innerHtml   trusted markup (never user input)
 */
function paintBanner(background, innerHtml) {
  clearBanner();

  const banner = document.createElement('div');
  banner.id = 'test-mode-banner';
  banner.setAttribute('role', 'status');
  // Styles live on the element ITSELF, not on an inner wrapper — the
  // wrapper was fixed while its parent stayed static and zero-height, so
  // there was nothing whose height could be measured.
  banner.style.cssText = `
    background: ${background};
    color: #ffffff;
    padding: 10px 16px;
    text-align: center;
    font-size: 0.875rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    position: fixed;
    top: 0; left: 0; right: 0;
    z-index: 9999;
    box-shadow: 0 2px 8px rgba(0,0,0,0.25);
    display: flex; align-items: center; justify-content: center;
    gap: 10px; flex-wrap: wrap;
  `;
  banner.innerHTML = `
    <span aria-hidden="true" style="
      display: inline-block; width: 8px; height: 8px; border-radius: 50%;
      background: #fff; box-shadow: 0 0 0 3px rgba(255,255,255,0.35);
      animation: testModePulse 1.6s ease-in-out infinite; flex-shrink: 0;
    "></span>
    ${innerHtml}
  `;
  // Inject pulse keyframes once.
  if (!document.getElementById('test-mode-banner-styles')) {
    const style = document.createElement('style');
    style.id = 'test-mode-banner-styles';
    style.textContent = `
      @keyframes testModePulse {
        0%, 100% { opacity: 1; transform: scale(1); }
        50% { opacity: 0.55; transform: scale(0.85); }
      }
    `;
    document.head.appendChild(style);
  }
  document.body.appendChild(banner);
  document.body.classList.add('has-env-banner');
  trackBannerHeight(banner);
}

/**
 * Render (or remove) the test mode banner.
 */
export function renderTestBanner() {
  if (!testModeActive) {
    clearBanner();
    return;
  }
  paintBanner(
    'var(--accent-500, #BD1B44)',
    t('banner.test_mode'),
  );
}

/**
 * Beta banner — shown on an app flagged `is_beta` in the app registry
 * (ISG Admin → Apps → Beta). Anyone seeing this bar is on the app's
 * testing team, because nobody else can load the app at all.
 *
 * ▶ REWRITTEN 2026-08-18. It used to read "BETA INSTANCE — you're testing
 *   pre-release changes on sandbox data", with an "Exit beta" link to
 *   `/api/beta-off`. Both halves are now false: there is no second
 *   instance and no sandbox database — beta is a flag on the live app —
 *   and `/api/beta-off` no longer exists, so the link would have been a
 *   dead route that unsets a cookie nothing reads. Telling a tester their
 *   data is a sandbox when it is production is the worst kind of stale
 *   banner, which is why this changed in the same commit as the backend.
 */
export function renderBetaBanner() {
  paintBanner(
    '#7c3aed',
    t('banner.beta'),
  );
}

/**
 * In-development banner — shown on the LIVE instance of an app flagged
 * `is_in_development` in the app registry (ISG Admin → Apps → In development).
 *
 * Different claim from the beta banner, which is why it is a different bar:
 * beta says "this instance holds sandbox data", this says "the app itself is
 * still being built". An app is in development on its way TO the beta/live
 * cycle, so the two are consecutive states, not the same one.
 *
 * No dismiss control, deliberately. The condition it reports persists for
 * weeks and the app changes under the user daily; a warning that stays
 * dismissed would be worse than none.
 */
export function renderDevBanner() {
  paintBanner(
    '#b45309',
    t('banner.in_development'),
  );
}

/**
 * One-call env banner wiring for app mains.
 *
 * Precedence when more than one state is true — one bar, so one message:
 *   beta  >  test mode  >  in development
 * Beta comes first because it is the only one of the three that says
 * something about who may be here at all; the others describe the data or
 * the app's maturity.
 *
 * ⚠️ `data.env` IS NO LONGER READ. It answered "which instance are you on"
 *   and always returns 'live' since the beta-instance model was retired on
 *   2026-08-18. The key is still emitted for compatibility, so a branch on
 *   it would not error — it would just never fire, which is why it was
 *   removed rather than left in place looking correct.
 */
export async function checkEnvBanner() {
  // The /beta-on and /beta-off rescue that used to live here is gone with
  // the routes themselves (2026-08-18). It existed because PWA service
  // workers navigate-fallback the app shell for any path, so a swallowed
  // toggle booted the SPA on a dead route; there is no toggle to swallow now.
  try {
    // i18n-fetch-ok: config GET, no user-visible server text
    const res = await fetch('/api/test-mode');
    if (!res.ok) return;
    const data = await res.json();
    if (data.beta === true) {
      renderBetaBanner();
    } else if (data.test_mode === true) {
      testModeActive = true;
      renderTestBanner();
    } else if (data.in_development === true) {
      renderDevBanner();
    }
  } catch { /* no banner */ }
}
